// src/components/superadmin/ClientSetupForm.jsx - FIXED: Removed Admin Users Section
import React, { useState } from 'react';
import { createClientConfiguration } from '../../api/superAdminService';

function ClientSetupForm({ onClientCreated, onCancel }) {
  const [formData, setFormData] = useState({
    salonName: '',
    customDomain: '',
    status: 'active',
    modules: {
      promotions: false,
      communications: false,
      bookings: false,
      reports: false,
      bespokeai: false
    }
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleModuleChange = (moduleId) => {
    setFormData(prev => ({
      ...prev,
      modules: {
        ...prev.modules,
        [moduleId]: !prev.modules[moduleId]
      }
    }));
  };

  const generateDomainSuggestion = (salonName) => {
    return salonName.toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .trim();
  };

  const handleSalonNameChange = (e) => {
    const salonName = e.target.value;
    setFormData(prev => ({
      ...prev,
      salonName,
      customDomain: generateDomainSuggestion(salonName)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Validation
      if (!formData.salonName.trim()) {
        throw new Error('Salon name is required');
      }
      
      if (!formData.customDomain.trim()) {
        throw new Error('Custom Domain is required');
      }

      // Submit without admin users - they will be added later via Configure page
      const result = await createClientConfiguration({
        ...formData,
        admins: [] // Empty array
      });
      
      console.log('Created client and salon:', result);
      onClientCreated();
    } catch (err) {
      console.error('Error creating client:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="client-setup-form">
      <div className="form-header">
        <h2>Create New Client Configuration</h2>
        <button type="button" onClick={onCancel} className="close-button">×</button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit}>
        {/* Basic Information */}
        <div className="form-section">
          <h3>Basic Information</h3>
          
          <div className="form-group">
            <label>Salon Name *</label>
            <input
              type="text"
              name="salonName"
              value={formData.salonName}
              onChange={handleSalonNameChange}
              required
              placeholder="e.g., Elegance Beauty Salon"
            />
          </div>

          <div className="form-group">
            <label>Custom Domain *</label>
            <input
              type="text"
              name="customDomain"
              value={formData.customDomain}
              onChange={handleInputChange}
              required
              placeholder="e.g., elegance-beauty"
            />
            <div className="form-help">
              This will be used as the document ID in both 'clients' and 'salons' collections.<br/>
              Admin URL: http://localhost:3000/BespokeBeauty/{formData.customDomain || 'elegance-beauty'}/admin/<br/>
              Customer URL: http://localhost:3000/BespokeBeauty/{formData.customDomain || 'elegance-beauty'}/customer/
            </div>
          </div>

          <div className="form-group">
            <label>Status *</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              required
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="pending">Pending</option>
            </select>
          </div>
        </div>

        {/* Module Selection */}
        <div className="form-section">
          <h3>Module Access</h3>
          <div className="modules-grid">
            <div className="module-item">
              <input
                type="checkbox"
                id="module-home"
                checked={true}
                disabled
              />
              <label htmlFor="module-home">
                <strong>Home Module</strong>
                <span>Core salon configuration and content management (Always enabled)</span>
              </label>
            </div>

            <div className="module-item">
              <input
                type="checkbox"
                id="module-promotions"
                checked={formData.modules.promotions}
                onChange={() => handleModuleChange('promotions')}
              />
              <label htmlFor="module-promotions">
                <strong>Promotions Management</strong>
                <span>Create and manage promotional campaigns and offers</span>
              </label>
            </div>

            <div className="module-item">
              <input
                type="checkbox"
                id="module-communications"
                checked={formData.modules.communications}
                onChange={() => handleModuleChange('communications')}
              />
              <label htmlFor="module-communications">
                <strong>Communications Management</strong>
                <span>Email and push notification campaigns</span>
              </label>
            </div>

            <div className="module-item">
              <input
                type="checkbox"
                id="module-bookings"
                checked={formData.modules.bookings}
                onChange={() => handleModuleChange('bookings')}
              />
              <label htmlFor="module-bookings">
                <strong>Bookings Management</strong>
                <span>Calendar management and booking oversight</span>
              </label>
            </div>

            <div className="module-item">
              <input
                type="checkbox"
                id="module-reports"
                checked={formData.modules.reports}
                onChange={() => handleModuleChange('reports')}
              />
              <label htmlFor="module-reports">
                <strong>Reports Management</strong>
                <span>Analytics, reporting, and business intelligence</span>
              </label>
            </div>

            <div className="module-item">
              <input
                type="checkbox"
                id="module-bespokeai"
                checked={formData.modules.bespokeai}
                onChange={() => handleModuleChange('bespokeai')}
              />
              <label htmlFor="module-bespokeai">
                <strong>Bespoke AI</strong>
                <span>AI-powered recommendations and automation</span>
              </label>
            </div>
          </div>
        </div>

        {/* Info Box about Admin Users */}
        <div className="form-section">
          <div className="info-box">
            <h4>ℹ️ Admin Users</h4>
            <p>Admin users can be added after client creation via the <strong>Configure → Admin Users</strong> tab.</p>
          </div>
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button type="button" onClick={onCancel} className="cancel-button">
            Cancel
          </button>
          <button type="submit" disabled={loading} className="submit-button">
            {loading ? 'Creating Client...' : 'Create Client'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ClientSetupForm;